import { useState } from 'react';
import { Form, Formik } from 'formik';
import { object, string } from 'yup';
import { toast } from 'react-toastify';
import Switch from 'react-switch';
import { FormikInput, FormikTextArea, Label } from '../common/Form';
import Button from '../common/Button';
import EventMediaUploader from './EventMediaUploader';
import { useEventSettingsContext } from '../../contexts/EventSettingsContext';

// FE-controlled option lists (per the design screenshot). Backend stores these as free text.
const STYLE_OF_EVENT_OPTIONS = ['Drop off catering', 'Full catering', 'On premise', 'Pick up catering'];
const HEARD_ABOUT_OPTIONS = ['Search Engine', 'Yelp', 'Instagram', 'Facebook', 'Email', 'Referred by Friend', 'Other'];

const SWITCH_PROPS = {
  height: 20,
  width: 45,
  handleDiameter: 18,
  uncheckedIcon: false,
  checkedIcon: false,
  onColor: '#000000',
  offColor: '#EEEEEE'
};

const EventSettingsSchema = object({
  sectionTitle: string().max(200, 'Section title must be 200 characters or fewer.'),
  eventsText: string().max(10000, 'Events text is too long.'),
  deckUrl: string().url('Enter a valid URL (including https://).').nullable(),
  notificationEmail: string().email('Enter a valid email address.').nullable()
});

// Static, disabled rendering of the customer-facing inquiry form (shown when the toggle is on).
const InquiryFormPreview = () => (
  <div className="inquiry-form-preview">
    <p className="inquiry-form-preview-title">Inquiry form preview</p>
    <div className="inquiry-form-preview-grid">
      <input disabled placeholder="First Name" />
      <input disabled placeholder="Last Name" />
      <input disabled placeholder="Email" />
      <input disabled placeholder="Phone" />
      <input disabled placeholder="Type of Event (e.g. Birthday Party, Business Dinner)" />
      <select disabled defaultValue="">
        <option value="" disabled>
          Style of Event
        </option>
        {STYLE_OF_EVENT_OPTIONS.map((option) => (
          <option key={option}>{option}</option>
        ))}
      </select>
      <input disabled placeholder="# of People" />
      <input disabled placeholder="Event Date / Time" />
      <select disabled defaultValue="">
        <option value="" disabled>
          How did you hear about us?
        </option>
        {HEARD_ABOUT_OPTIONS.map((option) => (
          <option key={option}>{option}</option>
        ))}
      </select>
      <textarea disabled placeholder="Message" className="inquiry-form-preview-message" />
    </div>
  </div>
);

const EventSettings = () => {
  const { settings, media, saveSettings, uploadMedia, removeMedia, reorderMedia } = useEventSettingsContext();
  const [isSaving, setIsSaving] = useState(false);

  if (!settings) {
    return null;
  }

  const handleSubmit = async (values: {
    isEventsEnabled: boolean;
    sectionTitle: string;
    eventsText: string;
    deckUrl: string;
    isInquiryFormEnabled: boolean;
    notificationEmail: string;
  }) => {
    setIsSaving(true);
    try {
      await saveSettings({
        isEventsEnabled: values.isEventsEnabled,
        sectionTitle: values.sectionTitle,
        eventsText: values.eventsText,
        // Send null (not '') so the backend clears the field instead of failing URL/email validation.
        deckUrl: values.deckUrl?.trim() ? values.deckUrl.trim() : null,
        isInquiryFormEnabled: values.isInquiryFormEnabled,
        notificationEmail: values.notificationEmail?.trim() ? values.notificationEmail.trim() : null
      });
      toast.success('Events settings saved.', { position: toast.POSITION.TOP_RIGHT });
    } catch (error) {
      toast.error('Could not save events settings. Please try again.', { position: toast.POSITION.TOP_RIGHT });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Formik
      initialValues={{
        isEventsEnabled: settings.isEventsEnabled,
        sectionTitle: settings.sectionTitle ?? '',
        eventsText: settings.eventsText ?? '',
        deckUrl: settings.deckUrl ?? '',
        isInquiryFormEnabled: settings.isInquiryFormEnabled,
        notificationEmail: settings.notificationEmail ?? ''
      }}
      validationSchema={EventSettingsSchema}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue }) => (
        <Form className="event-settings-form">
          <div className="event-settings-toggle-row">
            <Label label="ENABLE EVENTS PAGE" />
            <Switch
              /* eslint-disable-next-line react/jsx-props-no-spreading */
              {...SWITCH_PROPS}
              checked={values.isEventsEnabled}
              onChange={() => setFieldValue('isEventsEnabled', !values.isEventsEnabled)}
            />
          </div>
          <p className="event-settings-hint">Turn this on to make your Events page visible on your public profile.</p>

          <FormikInput name="sectionTitle" label="EVENT SECTION TITLE" placeholder="e.g. Bob's Crab Shack" />

          <FormikTextArea
            name="eventsText"
            label="EVENTS TEXT"
            message="Describe your event space and what you offer."
          />

          <div className="event-settings-media">
            <Label label="EVENT SECTION MEDIA" />
            <p className="event-settings-media-caption">
              Choose up to 10 photos or video for your Events page. The photos or video should showcase your event
              space.
            </p>
            <EventMediaUploader media={media} onUpload={uploadMedia} onDelete={removeMedia} onReorder={reorderMedia} />
          </div>

          <FormikInput name="deckUrl" label="LINK TO EVENTS DECK (OPTIONAL)" placeholder="https://..." />

          <div className="event-settings-toggle-row">
            <Label label="ADD EVENTS INQUIRY FORM (OPTIONAL)" />
            <Switch
              /* eslint-disable-next-line react/jsx-props-no-spreading */
              {...SWITCH_PROPS}
              checked={values.isInquiryFormEnabled}
              onChange={() => setFieldValue('isInquiryFormEnabled', !values.isInquiryFormEnabled)}
            />
          </div>
          {values.isInquiryFormEnabled && <InquiryFormPreview />}

          <FormikInput
            name="notificationEmail"
            label="NOTIFICATION EMAIL (OPTIONAL)"
            type="email"
            placeholder="Defaults to your manager email"
          />

          <Button className="event-settings-save" submit isLoading={isSaving}>
            SAVE
          </Button>
        </Form>
      )}
    </Formik>
  );
};

export default EventSettings;
